import { ChangeEvent, DragEvent, KeyboardEvent, MouseEvent, useEffect, useRef, useState, ReactNode, Fragment } from 'react';
import { classNames } from '../../utils';
import styles from './FileUpload.module.css';
import { FeaturedIcon } from '../../atoms/featured-icon';
import Text from '../../atoms/typography/Text';
import FileLineIcon from '../../atoms/icons/FileLineIcon';
import { Trans, useTranslation } from 'react-i18next';
import { ColorEnum, FontSizeEnum, FontWeightEnum } from '../../enums';
import BulletSeparatedList from '../../molecules/bullet-separated-list/BulletSeparatedList';
import formatBytes from '../../utils/formatBytes';
import ButtonNew from '../../molecules/button/Button';
import DeleteBinIcon from '../../atoms/icons/DeleteBinIcon';
import FormError from '../../molecules/form/FormError';
import { FormError as FormErrorType, TranslationFormError } from '../../molecules/form/Form';
import CheckCircleFillIcon from '../../atoms/icons/CheckCircleFillIcon';
import { FormattedFileName } from '../../atoms/formatted-file-name';

export type AcceptedExtension = 'png' | 'jpg' | 'jpeg' | 'pdf';

export enum FileUploadError {
    WRONG_FILE_TYPE = 'WRONG_FILE_TYPE',
    FILE_TOO_LARGE = 'FILE_TOO_LARGE',
    EMPTY_FILE = 'EMPTY_FILE',
    INVALID_FILENAME_CHARACTERS = 'INVALID_FILENAME_CHARACTERS',
    FILENAME_TOO_LONG = 'FILENAME_TOO_LONG',
    FILENAME_ALREADY_EXISTS = 'FILENAME_ALREADY_EXISTS',
}

export interface FileMetaData {
    name: string;
    size: number;
}

export interface FileUploadProps {
    name?: string;
    required?: boolean;
    multiple?: boolean;
    disabled?: boolean;
    storedFileMetadata?: FileMetaData[] | [];
    errors?: FormErrorType[];
    acceptedExtensions?: AcceptedExtension[];
    maxFileSizeInMbs?: number;
    subText?: ReactNode;
    onDelete?: (filename?: string) => void;
}

const FileUpload = ({
    name,
    required = false,
    multiple = false,
    disabled = false,
    storedFileMetadata = [],
    errors = [],
    acceptedExtensions,
    maxFileSizeInMbs = 10,
    subText = undefined,
    onDelete,
}: FileUploadProps) => {
    const { t } = useTranslation();
    const validationTranslation = useTranslation('validation');
    const vT = validationTranslation.t;

    const inputRef = useRef<HTMLInputElement>(null);
    const formInputRef = useRef<HTMLInputElement>(null);
    const dropzoneRef = useRef<HTMLDivElement>(null);
    const dragCounter = useRef(0);

    const [ fileUploadErrors, setFileUploadErrors ] = useState<FormErrorType[]>(errors);
    const [ hasNewFile, setHasNewFiles ] = useState(false);
    const [ previousStoredFileMetaData, setPreviousStoredFileMetaData ] = useState<(File | FileMetaData)[]>([]);
    const [ selectedFiles, setSelectedFiles ] = useState<(File | FileMetaData)[]>([]);
    const [ isDragging, setIsDragging ] = useState(false);
    const [ uploadPercentages, setUploadPercentages ] = useState<{[key: string]: number}>({});
    const [ isDeletingFile, setIsDeletingFile ] = useState<File | FileMetaData | null>(null);
    const [ hasDeletedFile, setHasDeletedFile ] = useState<boolean>(false);

    const maxFileSizeInBytes = maxFileSizeInMbs * 1000000;

    useEffect(() => {
        setPreviousStoredFileMetaData(storedFileMetadata);
        setSelectedFiles(storedFileMetadata);
    }, []);

    const areFileArraysEqual = (arr1, arr2) => {
        if (arr1 === arr2) return true;

        if (!arr1 || !arr2) return false;

        if (arr1.length !== arr2.length) return false;

        const serialize = file => `${file.name}:${file.size}`;

        const map1 = arr1.map(serialize).sort();
        const map2 = arr2.map(serialize).sort();

        return map1.every((val, idx) => val === map2[idx]);
    }

    useEffect(() => {
        if (!areFileArraysEqual(storedFileMetadata, previousStoredFileMetaData)) {
            if (!areFileArraysEqual(storedFileMetadata, selectedFiles)) {
                if (storedFileMetadata.length < selectedFiles.length && !storedFileMetadata.find((file: FileMetaData) => file.name === isDeletingFile?.name)) {
                    setHasDeletedFile(true);
                    setTimeout(() => {
                        setHasDeletedFile(false);
                        setSelectedFiles([
                            ...selectedFiles.filter(file => file.name !== isDeletingFile?.name)
                        ]);
                    }, 300);
                } else {
                    setHasDeletedFile(false);
                    setSelectedFiles(storedFileMetadata);
                }
            }
            setPreviousStoredFileMetaData(storedFileMetadata);
        }
    }, [storedFileMetadata]);

    useEffect(() => {
        const dt = new DataTransfer();

        Array.from(selectedFiles).forEach(file => {
            if (isNativeFile(file)) {
                dt.items.add(file as File);
            }
            if (isNewlyAddedFile(file)) {
                randomlySetUploadPercentage(file.name);
            }
        });

        formInputRef.current.files = dt.files;
        setHasNewFiles(dt.files.length > 0);

        formInputRef.current.dispatchEvent(new Event('input', { bubbles: true }));
    }, [selectedFiles]);

    const isNewlyAddedFile = (file: File | FileMetaData) => {
        if (storedFileMetadata === null) {
            return true;
        }

        return !storedFileMetadata.some((prevFile: File | FileMetaData) => prevFile.name === file.name);
    }

    const randomlySetUploadPercentage = (key: string) => {
        const intervalId = setInterval(() => {
            setUploadPercentages((prev) => {
                const currentPercentage = prev[key] || 0;
                const increment = Math.floor(Math.random() * 10) + 1;
                const newPercentage = Math.min(currentPercentage + increment, 100);

                if (newPercentage === 100) {
                    setTimeout(() => {
                        clearInterval(intervalId);
                        setUploadPercentages((prev) => ({
                            ...prev,
                            [key]: 100,
                        }));
                    }, 100);
                }

                return {
                    ...prev,
                    [key]: newPercentage,
                };
            });
        }, Math.random() * 100 + 50);
    };

    const isNativeFile = (file: File | FileMetaData) => {
        return typeof File !== 'undefined' && file instanceof File;
    }

    const mergeFileListsAndIgnoreDuplicates = (filelistA: (File | FileMetaData)[] | [] = [], filelistB: (File | FileMetaData)[] | [] = []): (File | FileMetaData)[] => {
        const fileArray: (File | FileMetaData)[] = [];
        const seen = new Set<string>();

        const reversedFileListA = [...filelistA].reverse();
        const merged = [...reversedFileListA, ...filelistB];

        const unique = merged.filter((file) => {
            const key = `${file.name}`;
            if (seen.has(key)) {
                return false;
            }

            seen.add(key);

            return true;
        });

        unique.reverse().forEach((file) => fileArray.push(file));

        return fileArray;
    }

    /**
     * Retrieve translation key based on nameKey:
     * if a translation key exists with an exact match of the nameKey, that key will be used
     * if a translation key exists with a * at the end and it matches the nameKey partially, that key will be used
     * Otherwise it will return the key as is
     */
    const getNamedValidationTranslationKey = (key: string, nameKey: string): string => {
        if (t(`${key}${nameKey}`, { defaultValue: null })) {
            return `${key}${nameKey}`;
        } else if (t(`${key}${nameKey.replace(/\*$/, '')}`, { defaultValue: null })) {
            return `${key}${nameKey.replace(/\*$/, '')}`;
        } else {
            return key;
        }
    }

    const validateFiles = (files: File[]): {[key: string]: FileUploadError[]} => {
        const fileErrors: {[key: string]: FileUploadError[]} = {};

        for (const file of files) {
            fileErrors[`${file.name}`] = [];

            if (selectedFiles.some(existingFile => existingFile.name === file.name)) {
                fileErrors[`${file.name}`].push(FileUploadError.FILENAME_ALREADY_EXISTS);
            }

            if (acceptedExtensions && acceptedExtensions.length > 0) {
                const fileExtension = file.name.split('.').pop()?.toLowerCase();
                if (!(fileExtension && acceptedExtensions.includes(fileExtension as AcceptedExtension))) {
                    fileErrors[`${file.name}`].push(FileUploadError.WRONG_FILE_TYPE);
                }
            }

            if (file.size == 0) {
                fileErrors[`${file.name}`].push(FileUploadError.EMPTY_FILE);
            }

            if (maxFileSizeInBytes && file.size > maxFileSizeInBytes) {
                fileErrors[`${file.name}`].push(FileUploadError.FILE_TOO_LARGE);
            }

            if (file.name.length > 255) {
                fileErrors[`${file.name}`].push(FileUploadError.FILENAME_TOO_LONG);
            }

            if (file.name.match(/[^a-zA-Z0-9_.\-+()[\]@!&, ]/)) {
                fileErrors[`${file.name}`].push(FileUploadError.INVALID_FILENAME_CHARACTERS);
            }

            if (fileErrors[`${file.name}`].length === 0) {
                delete fileErrors[`${file.name}`];
            }
        }


        return fileErrors;
    }

    function getInvalidFilenameChars(filename) {
        const invalidChars = filename.match(/[^a-zA-Z0-9_.\- ]/g);
        return invalidChars || [];
    }

    const renderFormattedFileNameTranslation = (filename: string, key: string, params: {[key: string]: string | number}): JSX.Element => {
        return (
            <Trans
                i18nKey={key}
                t={vT}
                components={[
                    <FormattedFileName
                        key="1"
                        filename={filename}
                        inline={true}
                    />
                ]}
                values={params}
            />
        )
    }

    const renderCustomErrorText = (errors: TranslationFormError[], filename: string) => {
        return (
            <>
                {renderFormattedFileNameTranslation(filename, 'file.specific.multiple', { filename })}
                <ul>
                    {errors.map((error, index) => (
                        <li key={index}>
                            {vT(error.key, error.params)}
                        </li>
                    ))}
                </ul>
            </>
        );
    }

    const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
        event.preventDefault();

        const dt = new DataTransfer();
        const files = Array.from(event.target.files || []);

        if (files.length > 1 && !multiple) {
            setFileUploadErrors([{
                key: getNamedValidationTranslationKey('file.multipleFilesNotAllowed', name)
            }]);

            return;
        }

        const errors = !isDeletingFile ? validateFiles(files) : {};
        const newFileUploadErrors: FormErrorType[] = [];

        Object.keys(errors).forEach((filename) => {
            const fileErrors = errors[filename];

            const thisFileErrors: TranslationFormError[] = [];

            for (const fileError of fileErrors) {
                let baseKey = '';
                let key = '';
                const params: {[key: string]: string | number} = { filename };
                switch (fileError) {
                    case FileUploadError.FILE_TOO_LARGE: {
                        key = 'file.tooLarge';
                        params.maxSize = formatBytes(maxFileSizeInBytes);

                        break;
                    }
                    case FileUploadError.FILENAME_TOO_LONG: {
                        key = 'file.nameTooLong';
                        params.maxLength = 255;

                        break;
                    }
                    case FileUploadError.INVALID_FILENAME_CHARACTERS: {
                        const chars = getInvalidFilenameChars(filename);
                        baseKey = 'file.specialChars';

                        if (chars.length > 1) {
                            baseKey = 'file.specialChars.plural';
                        }

                        key = baseKey;
                        params.chars = chars;

                        break;
                    }
                    case FileUploadError.WRONG_FILE_TYPE: {
                        if (!(acceptedExtensions && acceptedExtensions.length > 0)) {
                            break;
                        }

                        baseKey = 'file.typeError';

                        let extensions: string = acceptedExtensions[0];

                        if (acceptedExtensions.length > 1) {
                            baseKey = 'file.typeError.plural';
                            if (acceptedExtensions.length > 2) {
                                acceptedExtensions.slice(0, acceptedExtensions.length - 1).join(', ');
                            }
                            extensions = `${acceptedExtensions[0]} ${vT('general.and')} ${acceptedExtensions[acceptedExtensions.length - 1]}`;
                        }

                        key = baseKey;
                        params.extensions = extensions;

                        break;
                    }
                    case FileUploadError.EMPTY_FILE: {
                        key = 'file.emptyFile';

                        break;
                    }
                    case FileUploadError.FILENAME_ALREADY_EXISTS: {
                        key = 'file.nameAlreadyExists';
                        break;
                    }
                    default: {
                        break;
                    }
                }

                if (files.length > 1) {
                    key = `${key}.specific`;
                    if (fileErrors.length > 1) {
                        key = `${key}.specific.multiple`;
                    }
                }


                thisFileErrors.push({
                    key: getNamedValidationTranslationKey(key, name),
                    params
                });
            }

            if (thisFileErrors.length > 1) {
                if (files.length > 1) {
                    newFileUploadErrors.push({ text: renderCustomErrorText(thisFileErrors, filename) });
                } else {
                    for (const err of thisFileErrors) {
                        newFileUploadErrors.push({ text: renderFormattedFileNameTranslation(filename, err.key, err.params) });
                    }
                }
            } else {
                newFileUploadErrors.push(thisFileErrors[0]);
            }
        });

        setFileUploadErrors(newFileUploadErrors);

        files.forEach((file) => {
            if (!errors[`${file.name}`]) {
                dt.items.add(file);
            }
        });

        if (multiple) {
            let fileListA: (File | FileMetaData)[] | [] = selectedFiles;

            if (fileListA === null) {
                fileListA = [];
            }

            setSelectedFiles(mergeFileListsAndIgnoreDuplicates(fileListA, Array.from(dt.files)));
        } else if (files.length === 1) {
            setSelectedFiles(Array.from(dt.files));
        }

        setIsDeletingFile(null);
    };

    const handleDropzoneClick = (e: MouseEvent<HTMLDivElement>) => {
        e.preventDefault();

        if (disabled) {
            return;
        }

        dropzoneRef.current?.focus();
        inputRef.current?.click();
    };

    const handleDropzoneKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
        if (disabled) {
            return;
        }

        switch (e.key) {
            case 'Enter':
                e.preventDefault();
                inputRef.current?.click();
                break;
            case 'Escape':
                e.preventDefault();
                dropzoneRef.current?.blur();
                break;
            default:
                break;
        }
    };

    const handleDropzoneDragEnter = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();

        if (disabled) {
            return;
        }

        dragCounter.current += 1;
        setIsDragging(true);
    };

    const handleDropzoneDragLeave = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();

        if (disabled) {
            return;
        }

        dragCounter.current -= 1;
        if (dragCounter.current === 0) {
            setIsDragging(false);
        }
    };

    const handleDropzoneDragOver = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
    };

    const handleDropzoneDrop = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();

        if (disabled) {
            return;
        }

        dragCounter.current = 0;
        setIsDragging(false);

        if (e.dataTransfer.files.length > 0) {
            const dt = new DataTransfer();
            Array.from(e.dataTransfer.files).forEach((file) => dt.items.add(file));

            inputRef.current.files = dt.files;

            dispatchInputChangeEvent();

            return;
        }
    };

    const dispatchInputChangeEvent = () => {
        const event = new Event('change', { bubbles: true });
        inputRef.current.dispatchEvent(event);
    }

    const renderRequirements = (): ReactNode => {
        if (acceptedExtensions === undefined || acceptedExtensions.length === 0) {
            return null;
        }

        const requirements = [];

        if (acceptedExtensions.length > 0) {
            requirements.push(acceptedExtensions?.map(ext => `${ext.toUpperCase()}`).join(', '));
        }

        if (maxFileSizeInBytes) {
            requirements.push(
                t('file.input.max.fileSize', { fileSize: formatBytes(maxFileSizeInBytes) })
            );
        }

        if (requirements.length === 0) {
            return null;
        }

        return (
            <Text
                className={styles.requirements}
                size={FontSizeEnum.Sm}
                weight={FontWeightEnum.Light}
            >
                <BulletSeparatedList
                    list={requirements.map((item, index) => (
                        <span key={index}>{item}</span>
                    ))}
                    className={styles.additionalFileDetails}
                />
            </Text>
        );
    }

    const renderFileDetails = (file: File | FileMetaData): ReactNode => {
        const list = [
            <span key={1}>{formatBytes(file.size)}</span>,
        ];

        const uploadPercentage = Math.min(uploadPercentages[file?.name], 100) || 0;

        if (uploadPercentage > 0 && uploadPercentage < 100) {
            list.push(<span>{t('file.input.uploadPercentage', { uploadPercentage })}</span>);
        }
        if (uploadPercentage === 100) {
            list.push(<span>{t('file.input.uploaded')} <CheckCircleFillIcon className={styles.uploadedCheckIcon} /></span>);
        }

        return (
            <>
                <Text
                    className={styles.fileDetails}
                    size={FontSizeEnum.Sm}
                    weight={FontWeightEnum.Regular}
                >
                    <FormattedFileName
                        filename={file.name}
                    />
                </Text>
                <Text
                    className={styles.requirements}
                    size={FontSizeEnum.Sm}
                    weight={FontWeightEnum.Light}
                >
                    <BulletSeparatedList
                        list={list}
                        className={styles.additionalFileDetails}
                    />
                </Text>
            </>
        );
    }

    const handleDeleteClick = (e: MouseEvent, deleteFile: File | FileMetaData) => {
        if (!inputRef.current) {
            return;
        }

        setIsDeletingFile(deleteFile);

        e.stopPropagation();

        const isFileStoredFile = (storedFileMetadata.find((file: FileMetaData) => file.name === deleteFile.name) !== undefined);

        if (!isFileStoredFile) {
            setHasDeletedFile(true);
        }

        setTimeout(() => {
            inputRef.current.value = '';

            const dt = new DataTransfer();
            const filesArray: (File | FileMetaData)[] = [];

            onDelete?.(deleteFile.name);

            if (isFileStoredFile) {
                setHasDeletedFile(false);
                return;
            }

            setUploadPercentages({
                ...uploadPercentages,
                [deleteFile.name]: 0,
            })

            if (multiple) {
                Array.from(selectedFiles)
                    .filter(file =>
                        !(file.name === deleteFile.name)
                    )
                    .forEach(file => {
                        if (isNativeFile(file)) {
                            dt.items.add(file as File);
                        }
                        filesArray.push(file);
                    });
                setSelectedFiles(filesArray);
            } else if (storedFileMetadata.length === 0) {
                setSelectedFiles([]);
            }

            setTimeout(() => {
                inputRef.current.files = dt.files;

                dispatchInputChangeEvent();
                setHasDeletedFile(false);
            }, 1);
        }, 300);
    };

    const renderFileBox = (file: File | FileMetaData | null, showMultiple: boolean, isChildOfMultiUpload: boolean, hasFiles: boolean): ReactNode => {
        const uploadPercentage = Math.min(uploadPercentages[file?.name], 100) || 0;

        const fileBoxClassNames = classNames(
            styles.fileBox,
            showMultiple && hasFiles && styles.showMultipleFiles,
            hasFiles && styles.hasFile,
            storedFileMetadata.find((storedFile: FileMetaData) => storedFile.name === file?.name) && styles.storedFile,
            uploadPercentage >= 0 && uploadPercentage < 100 && styles.uploading,
            uploadPercentage === 100 && styles.uploaded,
            isChildOfMultiUpload && hasDeletedFile && (file?.name === isDeletingFile?.name) && styles.deleting,
            isChildOfMultiUpload && styles.childOfMultiUpload,
        );

        return (
            <div className={fileBoxClassNames}>
                {!showMultiple && (
                    <div
                        className={styles.uploadProgress}
                        style={{ width: `${uploadPercentage}%` }}
                    ></div>
                )}
                <div className={styles.featuredIconContainer}>
                    <FeaturedIcon
                        className={classNames(styles.featuredIcon)}
                        icon={<FileLineIcon />}
                    />
                </div>
                <div className={styles.fileUploadInformation}>
                    <div className={styles.fileUploadInformationWrapper}>
                        {(showMultiple || !hasFiles) ? (
                            <>
                                <Text
                                    className={styles.fileInputText}
                                    size={FontSizeEnum.Sm}
                                    weight={FontWeightEnum.Regular}
                                >{t('file.input.text')}</Text>
                                {renderRequirements()}
                            </>
                        ) : (file && hasFiles) && (
                            renderFileDetails(file)
                        )}
                    </div>
                    {!showMultiple && hasFiles && (
                        <div>
                            <ButtonNew
                                className={styles.deleteButton}
                                iconBefore={<DeleteBinIcon />}
                                size='sm'
                                variant='empty'
                                onClick={(e) => handleDeleteClick(e, file)}
                                noTabIndex
                            ></ButtonNew>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    const fileUploadClassNames = classNames(
        styles.fileUpload,
        disabled && styles.disabled,
        ([...fileUploadErrors, ...errors].length > 0) && styles.error,
    )

    const dropzoneClassNames = classNames(
        styles.dropzone,
        isDragging && styles.dragging,
    )

    return (
        <div className={fileUploadClassNames}>
            <div>
                <input
                    ref={inputRef}
                    type='file'
                    multiple={multiple}
                    onChange={handleFileChange}
                    hidden
                    accept={acceptedExtensions?.map(ext => `.${ext}`).join(',')}
                />
                <input
                    ref={formInputRef}
                    name={name}
                    type='file'
                    required={required && (hasNewFile || selectedFiles?.length === 0)}
                    multiple={multiple}
                    hidden
                    accept={acceptedExtensions?.map(ext => `.${ext}`).join(',')}
                />
                <div
                    className={dropzoneClassNames}
                    ref={dropzoneRef}
                    onClick={handleDropzoneClick}
                    onKeyDown={handleDropzoneKeyDown}
                    onDragEnter={handleDropzoneDragEnter}
                    onDragOver={handleDropzoneDragOver}
                    onDragLeave={handleDropzoneDragLeave}
                    onDrop={handleDropzoneDrop}
                    tabIndex={disabled ? undefined : 0}
                >
                    {renderFileBox(selectedFiles?.[0] ?? null, multiple, false, selectedFiles?.length > 0)}
                </div>
                {[...fileUploadErrors, ...errors].length > 0 &&
                    <FormError errors={[...fileUploadErrors, ...errors]} />
                }
            </div>
            {subText &&
                <Text size={FontSizeEnum.Sm} color={ColorEnum.Gray500}>{subText}</Text>
            }
            {multiple && selectedFiles?.length > 0 && (
                <>
                    {Array.from(selectedFiles).map((file) => (
                        <Fragment key={file.name}>
                            {renderFileBox(file, false, true, true)}
                        </Fragment>
                    ))}
                </>
            )}
        </div>
    )
};

export default FileUpload;
